/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/AppointmentRequestSections/CorporateIdentity/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useState, useEffect } from "react";
import styles from "./CorporateIdentity.module.css";
import { useTranslation } from "react-i18next";
import { ReactComponent as CompanyIcon } from "../../../assets/icons/CorporateIdentity/CompanyIcon.svg";
import FileUploadArea from "../../../modals/ProposalCreateModal/FileUploadArea";
import Select from "react-select";
import {
  getOperationEnvironmentOptions,
  getCompanySizeOptions,
  getCompanyGeneralActivityOptions,
} from "../../../data/selectOptions";
import {
  getSelectedOption,
  getSafeSelectedOption,
  resolveSectorClassificationFromTree,
  getSelectedOptionWithZeroFirst,
  createFileObjectFromUrl,
  selectStyles,
} from "../../../utils/common";
import { getReferenceData } from "../../../logic/companyEdit";
import { fetchSectors } from "../../../logic/register/index.js";
import { useNavigate } from "react-router";

const referencedDataSelectProps = {
  getOptionLabel: (option) => option.nombre,
  getOptionValue: (option) => String(option._id),
};

const CorporateIdentity = ({ company, getFieldValidation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sectors, setSectors] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [businessGroups, setBusinessGroups] = useState([]);
  const [logoFile, setLogoFile] = useState(
    createFileObjectFromUrl(company?.logo) || null,
  );

  const isMediumMobile = window.innerWidth >= 481 && window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth >= 320 && window.innerWidth <= 480;
  const isMobile = isMediumMobile || isSmallMobile;

  const operationEnvironmentOptions = getOperationEnvironmentOptions(t);
  const companySizeOptions = getCompanySizeOptions(t);
  const companyGeneralActivityOptions = getCompanyGeneralActivityOptions(t);

  useEffect(() => {
    const fetchData = async () => {
      const [businessGroupsData, sectorsData] = await Promise.all([
        getReferenceData("businessGroups", navigate),
        fetchSectors(navigate),
      ]);
      setBusinessGroups(
        Array.isArray(businessGroupsData) ? businessGroupsData : [],
      );
      setSectors(Array.isArray(sectorsData) ? sectorsData : []);
    };
    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (!sectors.length) {
      setSubsectors([]);
      setBranches([]);
      return;
    }

    const resolved = resolveSectorClassificationFromTree(
      sectors,
      company?.sectorEconomico,
      company?.subSectorSCIAN,
      company?.ramaSCIAN,
    );

    setSubsectors(resolved.subSectors);
    setBranches(resolved.branches);
  }, [
    sectors,
    company?.sectorEconomico,
    company?.subSectorSCIAN,
    company?.ramaSCIAN,
  ]);

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.corporateIdentity.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.commercialNameLabel")}
            <span className={styles.required}> *</span>
          </label>
          <div className={styles.inputWrapper}>
            <CompanyIcon className={styles.inputIcon} />
            <input
              type="text"
              className={`${styles.input} ${styles.inputWithIcon} ${
                getFieldValidation && getFieldValidation("nombreComercial")
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.corporateIdentity.commercialNamePlaceholder",
              )}
              value={company?.nombreComercial || ""}
              disabled={true}
              maxLength={100}
            />
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.socialReasonLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <CompanyIcon className={styles.inputIcon} />
            <input
              type="text"
              className={`${styles.input} ${styles.inputWithIcon} ${
                getFieldValidation && getFieldValidation("razonSocial")
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.corporateIdentity.socialReasonPlaceholder",
              )}
              value={company?.razonSocial || ""}
              disabled={true}
            />
          </div>
        </div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.economicSectorLabel")}
          </label>
          <Select
            value={getSafeSelectedOption(sectors, company?.sectorEconomico)}
            placeholder={t(
              "components.corporateIdentity.economicSectorPlaceholder",
            )}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "8px",
                fontSize: "14px",
                cursor: "pointer",
                borderColor:
                  getFieldValidation && getFieldValidation("sectorEconomico")
                    ? "#dc3545"
                    : baseStyles.borderColor,
                marginBottom: "-10px",
              }),
              container: (baseStyles) => ({
                ...baseStyles,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
            className="select__control"
            isDisabled={true}
            {...referencedDataSelectProps}
            options={sectors}
          />
        </div>
        <div
          className={`${styles.inputGroup} ${
            isMobile ? styles.mobileTopSpacing : ""
          }`}
        >
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.economicActivityLabel")}
          </label>
          <Select
            value={getSafeSelectedOption(branches, company?.ramaSCIAN)}
            placeholder={t(
              "components.corporateIdentity.economicActivityPlaceholder",
            )}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "8px",
                fontSize: "14px",
                cursor: "pointer",
                borderColor:
                  getFieldValidation && getFieldValidation("ramaSCIAN")
                    ? "#dc3545"
                    : baseStyles.borderColor,
                marginBottom: "-10px",
              }),
              container: (baseStyles) => ({
                ...baseStyles,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
            className="select__control"
            isDisabled={true}
            options={branches}
            {...referencedDataSelectProps}
          />
        </div>
      </div>
      <div className={`${styles.inputRow} ${styles.twoInputsRow}`}>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.subsectorLabel")}
          </label>
          <Select
            value={getSafeSelectedOption(subSectors, company?.subSectorSCIAN)}
            placeholder={t("components.corporateIdentity.subsectorPlaceholder")}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "8px",
                fontSize: "14px",
                cursor: "pointer",
                borderColor:
                  getFieldValidation && getFieldValidation("subSectorSCIAN")
                    ? "#dc3545"
                    : baseStyles.borderColor,
                marginBottom: "-10px",
              }),
              container: (baseStyles) => ({
                ...baseStyles,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
            className="select__control"
            isDisabled={true}
            options={subSectors}
            {...referencedDataSelectProps}
          />
        </div>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.businessGroupLabel")}
          </label>
          <Select
            value={getSafeSelectedOption(
              businessGroups,
              company?.grupoEmpresarial,
            )}
            placeholder={t(
              "components.corporateIdentity.businessGroupPlaceholder",
            )}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "8px",
                fontSize: "14px",
                cursor: "pointer",
                borderColor:
                  getFieldValidation && getFieldValidation("grupoEmpresarial")
                    ? "#dc3545"
                    : baseStyles.borderColor,
                marginBottom: "-10px",
              }),
              container: (baseStyles) => ({
                ...baseStyles,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
            className="select__control"
            isDisabled={true}
            options={businessGroups}
            {...referencedDataSelectProps}
          />
        </div>
      </div>
      <div
        className={`${styles.inputRow} ${styles.twoInputsRow} ${styles.logoAndCompanyActivityRow}`}
      >
        <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
          <label
            className={`${styles.inputLabel} ${
              isMobile ? styles.mobileLogoLabel : ""
            }`}
          >
            {t("components.corporateIdentity.logoLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={logoFile}
              isUploading={false}
              isDragging={false}
              error=""
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForMobile={isMobile}
              disabled={true}
            />
            <input
              id="logoFileInput"
              type="file"
              className={styles.fileInput}
              style={{ display: "none" }}
              disabled={true}
            />
          </div>
        </div>
        <div
          className={`${styles.inputGroup} ${styles.logoAndCompanyActivityContainer}`}
        >
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.companyGeneralActivityLabel")}
          </label>
          <Select
            value={getSelectedOptionWithZeroFirst(
              companyGeneralActivityOptions,
              company?.actividadGeneralDeLaEmpresa,
            )}
            placeholder={t(
              "components.corporateIdentity.companyGeneralActivityPlaceholder",
            )}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "8px",
                fontSize: "14px",
                cursor: "pointer",
                borderColor:
                  getFieldValidation &&
                  getFieldValidation("actividadGeneralDeLaEmpresa")
                    ? "#dc3545"
                    : baseStyles.borderColor,
                marginBottom: "-10px",
              }),
              container: (baseStyles) => ({
                ...baseStyles,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
            className="select__control"
            isDisabled={true}
            options={companyGeneralActivityOptions}
          />
        </div>
      </div>
      <div className={`${styles.inputRow} ${styles.twoInputsRow}`}>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.operationEnvironmentLabel")}
          </label>
          <Select
            value={getSelectedOption(
              operationEnvironmentOptions,
              company?.ambitoOperaciones,
            )}
            placeholder={t(
              "components.corporateIdentity.operationEnvironmentPlaceholder",
            )}
            options={operationEnvironmentOptions}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("ambitoOperaciones")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
          />
        </div>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.corporateIdentity.companySizeLabel")}
          </label>
          <Select
            value={getSelectedOption(
              companySizeOptions,
              company?.tamanoDeLaEmpresa,
            )}
            options={companySizeOptions}
            placeholder={t(
              "components.corporateIdentity.companySizePlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("tamanoDeLaEmpresa")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
          />
        </div>
      </div>
    </div>
  );
};

export default CorporateIdentity;
